import React, { useState } from 'react';
import {
  Column,
  Container,
  Section,
  Stack,
  Row,
} from 'src/components-v2/Layout';
import { CmsSectionalHeader } from 'src/sections';
import { RichTextSwitch } from 'src/containers';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { useIsDarkBackground } from 'src/contexts';
import { Icon } from 'src/components-v2/DataDisplay';
import { PlayButton } from 'src/components-v2/Inputs';
import {
  RichTextWrapper,
  Video,
  ColumnWrapper,
} from './CmsEmbeddedVideoCard.styled';
import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import ThemeProvider from 'threads5/themes/theme-provider';
import VideoCard, {
  EmbeddedVideoCardProvider,
  useEmbeddedVideoCard,
} from './VideoCard';
import VideoGate, { useVideoGate } from './VideoGate';
import Callout from 'threads5/Callout';

// 🐘
const CmsEmbeddedVideoCard = ({ fields, sys }) => {
  // because this is a "floating" component, we need to use the intrinsic mode and background color
  // this way when the component is nested in a background container, the mode and background color will be respected
  // an alternative approach to this would be to add an ignore list in the background component
  const mode = useCmsMode({
    mode: fields?.intrinsicMode ?? fields?.mode,
    backgroundColor:
      fields?.intrinsicBackgroundColor ?? fields?.backgroundColor,
  });
  const componentTheme = useComponentTheme(mode);
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });

  const bgColor =
    componentTheme.palette[fields?.backgroundColor] ||
    fields?.backgroundColor ||
    'white';
  const isDarkBackground = useIsDarkBackground(bgColor);
  const isReversed = fields?.layout === 'Text left / Video right';

  return (
    <ThemeProvider theme={componentTheme}>
      <Section
        component='section'
        className='embedded-video-card'
        sx={{
          background: bgColor,
          ...fields?.styles,
        }}
      >
        {fields?.header && <CmsSectionalHeader {...fields.header} />}
        <Container {...inspectorProps({ fieldId: 'internalTitle' })}>
          <Row>
            <Column xs={24} sm={24} lg={20} lgOffset={2}>
              <Row>
                <EmbeddedVideoCardProvider>
                  <VideoGate gate={fields?.gate}>
                    <EmbeddedVideoCardSwitch
                      fields={fields}
                      sys={sys}
                      isDarkBackground={isDarkBackground}
                      isReversed={isReversed}
                    />
                  </VideoGate>
                </EmbeddedVideoCardProvider>
              </Row>
            </Column>
          </Row>
        </Container>
      </Section>
    </ThemeProvider>
  );
};

const LeftColumn = ({
  layout,
  textSys,
  videoSys,
  isDarkBackground,
  ...fields
}) => {
  switch (layout) {
    case 'Video left / Text right':
      return (
        <VideoColumn
          fields={fields?.videoPlayer?.fields}
          sys={videoSys}
          isDarkBackground={isDarkBackground}
        />
      );
    case 'Text left / Video right':
      return <TextColumn fields={fields} sys={textSys} />;
    default:
      return null;
  }
};

const RightColumn = ({
  layout,
  textSys,
  videoSys,
  isDarkBackground,
  ...fields
}) => {
  switch (layout) {
    case 'Video left / Text right':
      return <TextColumn fields={fields} sys={textSys} />;
    case 'Text left / Video right':
      return (
        <VideoColumn
          fields={fields?.videoPlayer?.fields}
          sys={videoSys}
          isDarkBackground={isDarkBackground}
        />
      );
    default:
      return null;
  }
};

const TextColumn = ({ fields, sys }) => {
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  return (
    <Column xs={20} xsOffset={1} md={9} className='text-column'>
      <Stack className='text-stack'>
        <RichTextWrapper
          className='rich-text-wrapper'
          component='div'
          // NOTE: this is super hacky and should be fixed 👇
          {...{
            bgColor: fields?.backgroundColor || 'white',
          }}
          {...inspectorProps({ fieldId: 'body' })}
        >
          <RichTextSwitch
            sys={sys}
            content={fields?.body}
            nodes={{
              h2: {
                variant: 'h2',
                component: 'h3',
                fieldId: 'body',
              },
              h6: {
                variant: 'h6',
                component: 'h3',
                fieldId: 'body',
              },
              p: {
                variant: 'p',
                fieldId: 'body',
              },
            }}
          />
        </RichTextWrapper>
      </Stack>
    </Column>
  );
};

const VideoColumn = ({ fields, sys, isDarkBackground }) => {
  const url = fields?.url;
  const coverMedia =
    fields?.coverImage?.fields?.file?.url ??
    fields?.coverMedia?.[0]?.fields?.file?.url;
  const isLazy = fields?.isLazy ?? false;
  const isVideo =
    fields?.coverMedia?.some((asset) => {
      return asset?.fields?.file?.contentType.startsWith('video/');
    }) ?? false;
  const gate = useVideoGate();
  const [hasStarted, setHasStarted] = useState(false);
  const cover = coverMedia ? `${coverMedia}?fm=webp&w=1364&q=60` : true;

  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  return (
    <Column
      xs={24}
      sm={22}
      smOffset={1}
      md={12}
      sx={{ p: { xs: '0', md: '2px' } }} // IMO this is an acceptable amount of SX
      {...inspectorProps({ fieldId: 'internalTitle' })}
      className='video-column'
    >
      <div
        onClickCapture={(event) => {
          if (!gate || hasStarted) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          gate.requestPlayback(() => {
            setHasStarted(true);
          });
        }}
      >
        {isVideo ? (
          <Callout
            description='Cover videos are not supported'
            status='warning'
            title='Warning'
          />
        ) : (
          <Video
            isLazy={isLazy}
            isDarkBackground={isDarkBackground}
            className='video'
            url={url}
            playing={gate ? hasStarted : true}
            playIcon={<PlayButton playIcon={<Icon icon='ChevronRight' />} />}
            light={hasStarted ? false : cover}
          />
        )}
      </div>
    </Column>
  );
};

const EmbeddedVideoCardSwitch = ({
  fields,
  sys,
  isDarkBackground,
  isReversed,
}) => {
  const { isVideoPlaying } = useEmbeddedVideoCard();
  switch (fields?.layout) {
    case 'Text left / Video right':
    case 'Video left / Text right':
      return (
        <ColumnWrapper
          className='column-wrapper'
          isReversed={isReversed}
          isDarkBackground={isDarkBackground}
        >
          <LeftColumn
            textSys={sys}
            videoSys={fields?.videoPlayer?.sys}
            isDarkBackground={isDarkBackground}
            {...fields}
          />
          <RightColumn
            textSys={sys}
            videoSys={fields?.videoPlayer?.sys}
            isDarkBackground={isDarkBackground}
            {...fields}
          />
        </ColumnWrapper>
      );
    case 'Video background / Text overlay':
      return (
        <VideoCard isVideoPlaying={isVideoPlaying} sys={sys} fields={fields} />
      );
    default:
      return null;
  }
};

export default CmsEmbeddedVideoCard;
